import { useTranslation } from 'react-i18next';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './interactive/button.jsx';
import { ErrorRetryDialog } from './dialog/error-retry-dialog.jsx';
import { FilterBar } from './form/filter-bar.jsx';
import { filterItems, deriveFilterFields } from './../../../shared-web/utilities/filter-logic.js';
import { getDefaultSort, compareWithTieBreaker } from './../../../shared-web/utilities/sort-utils.js';
import { useDataTableResponsivePriority } from './DataTable/use-responsive-priority.js';
import { TableBody } from './DataTable/table-body.jsx';

/** @param {{ columns: any[], rows: any[], emptyMessage?: string, title?: string, addLabel?: string, error?: string, onRetry?: () => void, onAdd?: () => void, _addTo?: (row: any) => string, onEdit?: (row: any) => void, editTo?: (row: any) => string, onDelete?: (row: any) => void, canEditRow?: (row: any) => boolean, canDeleteRow?: (row: any) => boolean, renderActions?: (row: any) => any[], renderRow?: Function, rowLink?: (row: any) => string|undefined, onRowClick?: (row: any) => void, extraHeaderActions?: any, loading?: boolean, initialFilterClauses?: any[], onFilterChange?: (c: any[]) => void, returnToParam?: string, filterFieldOptions?: Record<string,string[]>, renderBatch?: number, hasMore?: boolean, onLoadMore?: () => void, loadingMore?: boolean, defaultFilterCurrency?: string, lockedFilterCurrency?: string }} props
 * @returns {JSX.Element}
 */
export function DataTable({ columns, rows, emptyMessage, title, addLabel, error, onRetry, onAdd, _addTo, onEdit, editTo, onDelete, canEditRow, canDeleteRow, renderActions, renderRow, rowLink, onRowClick, extraHeaderActions, loading, initialFilterClauses, onFilterChange, returnToParam, filterFieldOptions, renderBatch, hasMore, onLoadMore, loadingMore, defaultFilterCurrency, lockedFilterCurrency }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const effectiveReturnTo = returnToParam ?? encodeURIComponent(location.pathname + location.search);
	function appendReturnTo(path) {
		if (!effectiveReturnTo || !path) { return path; }
		const sep = path.includes('?') ? '&' : '?';
		return path + sep + 'returnTo=' + effectiveReturnTo;
	}
	const [sortKey, setSortKey] = useState(() => getDefaultSort(columns).key);
	const [sortDir, setSortDir] = useState(() => getDefaultSort(columns).direction);
	const [filterClauses, setFilterClauses] = useState(/** @type {any[]} */ (initialFilterClauses || []));
	const [filterOpen, setFilterOpen] = useState(false);
	const [visibleCount, setVisibleCount] = useState(() => renderBatch ? Math.min(renderBatch, rows.length) : rows.length);
	useEffect(() => {
		if (!renderBatch) { return; }
		setVisibleCount(Math.min(renderBatch, rows.length));
	}, [sortKey, sortDir, filterClauses, renderBatch, rows.length]);
	const loadMore = useCallback(() => {
		if (!renderBatch) { return; }
		setVisibleCount((p) => Math.min(p + renderBatch, rows.length));
	}, [renderBatch, rows.length]);
	const filterFields = useMemo(() => {
		const fields = deriveFilterFields(columns);
		if (filterFieldOptions) {
			for (const f of fields) { if (filterFieldOptions[f.key]) { f.options = filterFieldOptions[f.key]; } }
		}
		return fields;
	}, [columns, filterFieldOptions]);
	const hasFilterable = filterFields.length > 0;
	function handleSort(key) {
		if (sortKey === key) { setSortDir((d) => d === 1 ? -1 : 1); } else { setSortKey(key); setSortDir(1); }
	}
	function getActionItems(row) {
		let items;
		if (renderActions) { items = renderActions(row); } else {
			items = [];
			if (onEdit && (!canEditRow || canEditRow(row))) { items.push({ label: t('btn.edit'), to: editTo ? editTo(row) : undefined, onClick: () => onEdit(row) }); }
			if (onDelete && (!canDeleteRow || canDeleteRow(row))) { items.push({ label: t('btn.delete'), onClick: () => onDelete(row), danger: true }); }
		}
		if (effectiveReturnTo) { items = items.map((it) => it.to ? { ...it, to: appendReturnTo(it.to) } : it); }
		return items;
	}
	const filteredRows = useMemo(() => {
		if (filterClauses.length === 0) { return rows; }
		return filterItems(rows, filterClauses, { filterFields });
	}, [rows, filterClauses, filterFields]);
	const sortedRows = useMemo(() => {
		if (!sortKey) { return filteredRows; }
		const col = columns.find((c) => c.key === sortKey);
		const getValue = col ? (col.getSortValue ?? col.getValue) : null;
		if (!getValue) { return filteredRows; }
		return [...filteredRows].sort((a, b) => {
			const tb = col.getSortTieBreaker ? { getValue: col.getSortTieBreaker, ...(col.tieBreakerDirection !== undefined ? { direction: col.tieBreakerDirection } : {}) } : undefined;
			return compareWithTieBreaker(a, b, getValue, sortDir, tb);
		});
	}, [filteredRows, sortKey, sortDir, columns]);
	const effectiveRowCount = renderBatch ? visibleCount : sortedRows.length;
	const handleFilterChange = useCallback((clauses) => {
		setFilterClauses(clauses);
		if (onFilterChange) { onFilterChange(clauses); }
	}, [onFilterChange]);
	const maxVisiblePriority = useDataTableResponsivePriority();
	return (
		<div className="card">
			<div className="card-header">
				<h2>{title || t('dataTable.title')}</h2>
				<div className="card-header-actions">
					{rows.length > 0 && (<span className="row-count">{hasMore ? `${sortedRows.length} of \u221E` : `${sortedRows.length} / ${rows.length}`}</span>)}
					{hasFilterable && (
						<Button variant="ghost" size="sm" className={`btn-filter${filterOpen ? ' btn-filter-active' : ''}`} onClick={() => setFilterOpen((o) => !o)} aria-label={t('aria.toggleFilters')}>
							<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 2h14l-5 6v5l-4 2v-7z" /></svg>
						</Button>
					)}
					{extraHeaderActions}
					{onAdd && (<Button variant="primary" size="sm" onClick={onAdd}>{addLabel || t('btn.add')}</Button>)}
				</div>
			</div>
			{hasFilterable && (
				<FilterBar columns={columns} items={rows} onFilterChange={handleFilterChange} filterFields={filterFields} open={filterOpen} onToggle={setFilterOpen} initialClauses={initialFilterClauses} defaultCurrency={defaultFilterCurrency} lockedCurrency={lockedFilterCurrency} />
			)}
			<TableBody columns={columns} sortedRows={sortedRows} sortKey={sortKey} sortDir={sortDir} handleSort={handleSort} maxVisiblePriority={maxVisiblePriority} loading={loading} error={error} filterClauses={filterClauses} handleFilterChange={handleFilterChange} emptyMessage={emptyMessage} renderRow={renderRow} getActionItems={getActionItems} appendReturnTo={appendReturnTo} rowLink={rowLink} navigate={navigate} effectiveReturnTo={effectiveReturnTo} onRowClick={onRowClick} visibleCount={effectiveRowCount} onLoadMore={loadMore} renderBatch={renderBatch} hasMorePages={hasMore} onFetchMore={onLoadMore} loadingMore={loadingMore} />
			{error && onRetry && <ErrorRetryDialog error={error} onRetry={onRetry} />}
		</div>
	);
}
